import type { LoadMode } from "./types.js";

export const MAX_EXERCISE_LOAD = 10_000;
export const MAX_EXERCISE_REPS = 1_000;
export const MAX_EXERCISE_COUNT = 100_000;

export interface ExerciseSetValues {
  weightValue: number | null;
  reps: number | null;
  countValue: number | null;
}

function wholeNumberBetween(
  value: number | null,
  minimum: number,
  maximum: number,
) {
  return (
    value !== null &&
    Number.isSafeInteger(value) &&
    value >= minimum &&
    value <= maximum
  );
}

function finiteLoad(value: number | null, allowZero: boolean) {
  return (
    value !== null &&
    Number.isFinite(value) &&
    value <= MAX_EXERCISE_LOAD &&
    (allowZero ? value >= 0 : value > 0)
  );
}

/** One numeric contract used before local persistence and again by the server. */
export function exerciseSetValueError(
  loadMode: LoadMode,
  values: ExerciseSetValues,
) {
  if (loadMode === "count")
    return values.weightValue === null &&
      values.reps === null &&
      wholeNumberBetween(values.countValue, 1, MAX_EXERCISE_COUNT)
      ? null
      : `Enter a whole completed count from 1 to ${MAX_EXERCISE_COUNT.toLocaleString("en-AU")}.`;

  if (loadMode === "bodyweight") {
    if (!finiteLoad(values.weightValue, true))
      return `Added load must be from 0 to ${MAX_EXERCISE_LOAD.toLocaleString("en-AU")} kg.`;
    if (!wholeNumberBetween(values.reps, 1, MAX_EXERCISE_REPS))
      return `Enter whole reps from 1 to ${MAX_EXERCISE_REPS.toLocaleString("en-AU")}.`;
    if (values.countValue !== null)
      return "Bodyweight exercises cannot include a count value.";
    return null;
  }

  if (!finiteLoad(values.weightValue, false))
    return `Enter a weight greater than 0 and no more than ${MAX_EXERCISE_LOAD.toLocaleString("en-AU")} kg.`;
  if (!wholeNumberBetween(values.reps, 1, MAX_EXERCISE_REPS))
    return `Enter whole reps from 1 to ${MAX_EXERCISE_REPS.toLocaleString("en-AU")}.`;
  if (values.countValue !== null)
    return "Loaded exercises cannot include a count value.";
  return null;
}
